"use client";

import { ColumnDef } from "@tanstack/react-table";
import { Branch } from "@/context/branch-context";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { usePutUpdateBranchFlags } from "@/api/pizza-checker/biteberry/biteberry";
import { toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";

/** The branch flags a switch can toggle; the label is only for display. */
type BranchFlag = "acquisition" | "bbTrigger" | "evaluation";

const FlagToggle = ({ 
  branch, 
  value, 
  flag,
  onUpdate
}: { 
  branch: Branch; 
  value: boolean; 
  flag: BranchFlag;
  onUpdate?: () => void;
}) => {
  const { t } = useLocalization();
  const label = t(`branches.flags.${flag}`);
  const [isToggling, setIsToggling] = useState(false);
  
  const updateBranchFlags = usePutUpdateBranchFlags({
    mutation: {
      onSuccess: () => {
        onUpdate?.();
        toast.success(
          t(value ? "branches.flags.disabled" : "branches.flags.enabled", {
            flag: label,
            branch: branch.name,
          }),
        );
        setIsToggling(false);
      },
      onError: (error) => {
        console.error('Failed to update branch flags:', error);
        toast.error(t("branches.flags.updateFailed", { flag: label }));
        setIsToggling(false);
      },
    },
  });

  const handleToggle = async () => {
    if (isToggling) return;
    
    setIsToggling(true);
    try {
      await updateBranchFlags.mutateAsync({
        bbBranchId: branch.bb_branch_id,
        data: {
            enable_acquisition: flag == "acquisition" ? !value : branch.enable_acquisition,
            enable_bb_trigger: flag == "bbTrigger" ? !value : branch.enable_bb_trigger,
            enable_evaluation: flag == "evaluation" ? !value : !value
        }
      })
    } catch {
      setIsToggling(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <Switch
        checked={value}
        onCheckedChange={handleToggle}
        disabled={isToggling}
        className="data-[state=checked]:bg-green-600"
      />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
};

const BranchActions = ({ 
  branch, 
  selectBranch 
}: { 
  branch: Branch; 
  selectBranch: (branchId: string | null) => void; 
}) => {
  const { t } = useLocalization();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">{t("branches.table.openMenu")}</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => selectBranch(branch.id)}>
          {t("branches.actions.switchTo")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const columns = (
  _getLink?: (branchId: string) => string,
  selectBranch?: (branchId: string | null) => void,
  onUpdate?: () => void,
): ColumnDef<Branch>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={translate("branches.table.selectAll")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={translate("branches.table.selectRow")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} label={translate("branches.columns.name")} />,
    cell: ({ row }) => {
      const branch = row.original;
      return (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{branch.name}</span>
          {branch.enabled && (
            <Badge variant="secondary" className="text-xs">
              {translate("branches.status.active")}
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "bb_branch_id",
    header: ({ column }) => <SortableHeader column={column} label={translate("branches.columns.bbBranchId")} />,
    cell: ({ getValue }) => {
      const bbBranchId = getValue() as string;
      return (
        <span className="font-mono text-sm">
          {bbBranchId}
        </span>
      );
    },
  },
  {
    accessorKey: "enable_acquisition",
    header: () => translate("branches.columns.acquisition"),
    cell: ({ row }) => {
      const branch = row.original;
      return (
        <FlagToggle
          branch={branch}
          value={branch.enable_acquisition}
          flag="acquisition"
          onUpdate={onUpdate}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "enable_bb_trigger",
    header: () => translate("branches.columns.bbTrigger"),
    cell: ({ row }) => {
      const branch = row.original;
      return (
        <FlagToggle
          branch={branch}
          value={branch.enable_bb_trigger}
          flag="bbTrigger"
          onUpdate={onUpdate}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "enable_evaluation",
    header: () => translate("branches.columns.evaluation"),
    cell: ({ row }) => {
      const branch = row.original;
      return (
        <FlagToggle
          branch={branch}
          value={branch.enable_evaluation}
          flag="evaluation"
          onUpdate={onUpdate}
        />
      );
    },
    enableSorting: false,
  },
  {
    id: "actions",
    header: () => translate("common.actions"),
    cell: ({ row }) => {
      const branch = row.original;
      return selectBranch ? (
        <BranchActions branch={branch} selectBranch={selectBranch} />
      ) : null;
    },
    enableSorting: false,
    enableHiding: false,
  },
];